// components/footer.jsx

import clsx from "clsx";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import { useEffect } from "react";
import BookButton from "@/components/ui/BookButton";
import FooterLinks from "./sections/footer-links";
import { FaArrowPointer } from "react-icons/fa6";
import { FaArrowRight, FaShieldAlt } from "react-icons/fa";
import { MdPhone } from "react-icons/md";

const Testimonials = dynamic(() => import("./sections/testimonials"), {
  ssr: false,
});

export default function Footer() {
  const router = useRouter();

  useEffect(() => {
    const el = document.querySelector(".animated-bg-gradient");
    if (!el) return;

    // switch to a deeper base and richer stops for more saturation
    el.style.backgroundColor = "#0629C8"; // darker brand blue

    const data = [
      { color: "#0A65F1", x: 20, y: 30 },
      { color: "#049FF2", x: 80, y: 25 },
      { color: "#0648E2", x: 30, y: 75 },
      { color: "#fc3b09", x: 70, y: 65 },
      { color: "#fb155f", x: 50, y: 85 },
    ];

    let t = 0;
    function animate() {
      t += 0.5;
      const gradients = data.map(({ color, x, y }) => {
        const nx = (x + 8 * Math.sin(((t + x) * Math.PI) / 180) + 100) % 100;
        const ny = (y + 8 * Math.cos(((t + y) * Math.PI) / 180) + 100) % 100;
        return `radial-gradient(at ${nx}% ${ny}%, ${color} 0%, transparent 40%)`;
      });
      el.style.backgroundImage = gradients.join(", ");
      requestAnimationFrame(animate);
    }

    animate();
  }, [router.pathname]);

  const serviceCTAs = {
    "/web-design": {
      heading: "Transform Your Online Presence",
      description:
        "Our web design services create visually stunning, user-friendly sites that drive conversions. Let’s build something great together.",
      buttonText: "Get a Free Web Design Quote",
    },
    "/seo": {
      heading: "Dominate Search Rankings",
      description:
        "Our SEO expertise will get your site found by the right audience. Enhance your online visibility with targeted strategies.",
      buttonText: "Boost My Rankings",
    },
    "/vip-support": {
      heading: "Experience VIP Support",
      description:
        "Get premium support and tailored solutions designed to keep your business running smoothly.",
      buttonText: "Book a 15‑min strategy call",
    },
    "/paid-search": {
      heading: "Maximize Your PPC ROI",
      description:
        "Our paid search campaigns are optimized to drive targeted traffic and deliver measurable results. Let’s amplify your ad spend.",
      buttonText: "Book a Free PPC Consultation",
    },
    "/social-media-marketing": {
      heading: "Engage Your Audience on Social Media",
      description:
        "From creative content to strategic ad placements, our social media marketing services will connect your brand with a wider audience.",
      buttonText: "Book a Free Social Media Consultation",
    },
    "/email-marketing": {
      heading: "Elevate Your Email & SMS Campaigns",
      description:
        "Drive engagement and conversions with our expertly crafted email marketing strategies. We create emails that resonate.",
      buttonText: "Book a Free Email Consultation",
    },
    "/bigcommerce": {
      heading: "Power Your BigCommerce Store",
      description:
        "Get a custom BigCommerce solution tailored to your business needs. Boost sales with a site designed for performance and scalability.",
      buttonText: "Book a Free BigCommerce Consultation",
    },
    "/shopify": {
      heading: "Build a Stunning Shopify Store",
      description:
        "Our Shopify expertise will help you create an online store that not only looks great but converts visitors into loyal customers.",
      buttonText: "Book a Free Shopify Consultation",
    },
    "/bigcommerce-to-shopify": {
      heading: "Ready to Transform Your Store?",
      description:
        "Let our experts handle the heavy lifting while you grow your business.",
      buttonText: "Migrate My Store Now",
    },
  };

  const defaultCTA = {
    heading: "Contact us today",
    description:
      "We can't wait to help you grow your business. Our team is ready to help you achieve your goals. Contact us today to get started.",
    buttonText: "Book a Consultation",
  };

  const currentCTA = serviceCTAs[router.pathname] || defaultCTA;

  return (
    <div>
      <div className="rounded-3xl bg-white">
        {(router.pathname === "/about" ||
          router.pathname === "/" ||
          router.pathname === "/tampa" ||
          router.pathname === "/ups") && <Testimonials />}

        {router.pathname.includes("/tools") ||
        router.pathname.includes("/contact-us") ||
        router.pathname.includes("/thank-you") ||
        router.pathname.includes("/careers") ||
        router.pathname.includes("/conversion-rate-optimization") ||
        router.pathname.includes("/local-listing-management") ? null : (
          <section className="animated-bg-gradient py-16 sm:py-20 lg:py-24">
            <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
              <h2 className="text-shadow-sm mb-6 text-3xl font-bold text-white sm:mb-8 sm:text-4xl lg:text-5xl">
                {currentCTA.heading}
              </h2>
              <p className="text-shadow-sm mb-8 text-lg leading-relaxed text-white/90 sm:mb-10 sm:text-xl">
                {currentCTA.description}
              </p>

              <div className="mb-8 flex flex-col justify-center gap-4 sm:flex-row sm:gap-6">
                <BookButton
                  color="orange"
                  text={currentCTA.buttonText}
                  custom="!text-center flex justify-center px-8 py-4 text-lg font-semibold"
                  right
                  icon={<FaArrowRight />}
                />

                <BookButton
                  color="white"
                  text="Call (813) 556-7990"
                  link="tel:8135567990"
                  custom="!text-center flex justify-center px-8 py-4 text-lg font-semibold"
                  left
                  icon={<MdPhone />}
                />
              </div>

              <div className="text-center text-white/80">
                <p className="text-shadow-sm text-sm sm:text-base">
                  <FaShieldAlt className="relative bottom-px mr-2 inline" />
                  No long-term contracts • Cancel anytime
                </p>
              </div>
            </div>
          </section>
        )}

        <FooterLinks />
      </div>
    </div>
  );
}
